import { useState, useEffect, useCallback } from 'react';

interface ScrollPosition {
  x: number;
  y: number;
  direction: 'up' | 'down' | null;
  isScrolled: boolean;
}

/**
 * Custom hook to track scroll position and direction
 * @param threshold - Pixel threshold to consider "scrolled" (default: 20)
 */
export function useScrollPosition(threshold = 20): ScrollPosition {
  const [scrollPosition, setScrollPosition] = useState<ScrollPosition>({
    x: 0,
    y: 0,
    direction: null,
    isScrolled: false,
  });

  const handleScroll = useCallback(() => {
    const currentY = window.scrollY;
    const currentX = window.scrollX;

    setScrollPosition((prev) => ({
      x: currentX,
      y: currentY,
      direction: currentY > prev.y ? 'down' : currentY < prev.y ? 'up' : prev.direction,
      isScrolled: currentY > threshold,
    }));
  }, [threshold]);

  useEffect(() => {
    // Set initial position
    handleScroll();

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [handleScroll]);

  return scrollPosition;
}

export default useScrollPosition;
